import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@mui/material';
import { HtmlViewerProvider, useHtmlViewer } from './HtmlViewerContext';
import { HtmlViewerToolbar } from './HtmlViewerToolbar';
import { HtmlViewerContent } from './HtmlViewerContent';
import { HtmlViewerHighlightLayer } from './HtmlViewerHighlightLayer';
import { HtmlViewerMemoMenu } from './HtmlViewerMemoMenu';
import { useGestureStateMachine } from './hooks/useGestureStateMachine';
import type { EliceHtmlViewerProps } from './HtmlViewerTypes';

// ─── Inner component (inside Provider) ───────────────────────────────────────

interface HtmlViewerInnerProps {
  content: string;
  documentId: string;
  colors?: string[];
  sx?: EliceHtmlViewerProps['sx'];
}

const HtmlViewerInner = ({
  content,
  documentId,
  colors,
  sx,
}: HtmlViewerInnerProps) => {
  const { state, dispatch } = useHtmlViewer();
  const contentRef = useRef<HTMLDivElement>(null);
  const surfaceRef = useRef<HTMLDivElement>(null);
  const [scrollHeight, setScrollHeight] = useState(0);

  const isHighlightMode = useCallback(
    () => state.mode === 'highlight',
    [state.mode]
  );

  const gestureHandlers = useGestureStateMachine({
    dispatch,
    isHighlightMode,
    contentRef,
    surfaceRef,
  });

  const handleContentMount = useCallback((node: HTMLDivElement | null) => {
    (contentRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
  }, []);

  useEffect(() => {
    const surface = surfaceRef.current;
    if (!surface) return;

    const recompute = () => {
      setScrollHeight(surface.scrollHeight);
    };

    const observer = new ResizeObserver(recompute);
    observer.observe(surface);

    if (contentRef.current) {
      observer.observe(contentRef.current);
    }

    recompute();
    return () => observer.disconnect();
  }, [content]);

  // Close menu on click outside highlights/menu
  const handleViewerClick = useCallback(() => {
    if (state.menuPosition && !state.pendingRange && !state.activeAnnotationId) {
      dispatch({ type: 'SET_MENU_POSITION', payload: null });
    }
  }, [state.menuPosition, state.pendingRange, state.activeAnnotationId, dispatch]);

  return (
    <Box
      sx={[
        {
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          overflowY: state.mode === 'highlight' ? 'hidden' : 'auto',
          touchAction: state.mode === 'highlight' ? 'none' : 'pan-y',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      onClick={handleViewerClick}
    >
      <HtmlViewerToolbar />

      <Box
        ref={surfaceRef}
        sx={{
          position: 'relative',
          flex: 1,
          p: 2,
          ...(state.mode === 'highlight' && {
            userSelect: 'none',
            WebkitUserSelect: 'none',
            WebkitTouchCallout: 'none',
            WebkitTapHighlightColor: 'transparent',
            touchAction: 'none',
          }),
        }}
        {...(state.mode === 'highlight' ? gestureHandlers : {})}
      >
        <HtmlViewerContent
          ref={node => {
            handleContentMount(node);
          }}
          content={content}
        />

        <HtmlViewerHighlightLayer
          contentRef={contentRef}
          geometryRootRef={surfaceRef}
          scrollHeight={scrollHeight}
        />
      </Box>

      <HtmlViewerMemoMenu
        contentRef={contentRef}
        documentId={documentId}
        colors={colors}
      />
    </Box>
  );
};

// ─── Public component ─────────────────────────────────────────────────────────

/**
 * Cross-platform HTML viewer with highlight & memo annotations.
 *
 * @security The `content` prop must be pre-sanitized by the caller before
 * passing to this component. No internal HTML sanitization is performed.
 */
const EliceHtmlViewer = ({
  content,
  documentId,
  defaultMode,
  annotations,
  onAnnotationCreate,
  onAnnotationUpdate,
  onAnnotationDelete,
  colors,
  sx,
}: EliceHtmlViewerProps) => {
  return (
    <HtmlViewerProvider
      defaultMode={defaultMode}
      annotations={annotations}
      onAnnotationCreate={onAnnotationCreate}
      onAnnotationUpdate={onAnnotationUpdate}
      onAnnotationDelete={onAnnotationDelete}
    >
      <HtmlViewerInner
        content={content}
        documentId={documentId}
        colors={colors}
        sx={sx}
      />
    </HtmlViewerProvider>
  );
};

export default EliceHtmlViewer;
